<script>
export const pageName = 'rockets-edit';
export const pageRoute = '/rockets/{rocketId}/edit';
export const title = 'Edit Rocket'
export const requireMembership = true;

import { hideLoading, showLoading } from '../../js/utils/loading.js';
import { navigate } from '@trullock/page-manager'
import { commands } from '../../js/lib/functions.js';
import { showToast } from '../../js/utils/toast.js'
import { getProjection } from '@trullock/firebase-client';
import UserView from '../../../functions/domain/users/userView.js';
import RocketView from '../../../functions/domain/rockets/rocketView.js';
import { getCurrentUserId } from '../../js/auth.js';
import { nextTick } from 'vue';
import { cropImage } from '../../js/utils/cropper.js';

export default {
	data() {
		return { 
			name : null,
			buildType: '',
			diameter: null,
			length: null,
			cp: null,
			mass: null,
			notes: null,
			isDirty: false,
			photo: null,
			existingPhotoUrl: null,
			ready: false
		}
	},

	methods: {
		beforeHide() {
			return this.isDirty ? 'Are you sure you want to abandon your changes?' : null
		},
		async show(opts) {
			
			commands.warmUp();
			
			this.rocket = await getProjection([UserView, getCurrentUserId(), RocketView, opts.rocketId])

			this.name = this.rocket.name
			this.buildType = this.rocket.buildType
			this.diameter = this.rocket.diameter
			this.length = this.rocket.length
			this.cp = this.rocket.cp
			this.mass = this.rocket.mass
			this.notes = this.rocket.notes
			this.photo = this.rocket.photoUrl;
			this.newPhoto = false;
			this.isDirty = false;

			this.ready = true;
			await nextTick()
			
			this.$refs.fupPhoto.value = null;
			this.$refs.fupPhoto.setCustomValidity('');
			this.$refs.lblPhoto.innerHTML = "Choose image&hellip;";

		},
		changed() {
			this.isDirty = true;
		},
		async submit () {
			showLoading();

			let result = await commands.rockets.update({ 
				id: this.rocket.id,
				name: this.name,
				buildType: this.buildType,
				diameter: parseInt(this.diameter || '0', 10) || null,
				length: parseInt(this.length || '0', 10) || null,
				cp: parseInt(this.cp || '0', 10) || null,
				mass: parseInt(this.mass || '0', 10) || null,
				notes: this.notes,
				photo: this.newPhoto ? this.photo : null
			});

			if(!result.success)
			{
				showToast({ message: 'Error editing rocket', style: 'bg-danger'})
				hideLoading();
				return;
			}

			this.isDirty = false;

			navigate(`/rockets/${this.rocket.id}`)

			showToast({
				message: 'Rocket updated',
				style: 'bg-success'
			})

			hideLoading();
		},
		photoAdded(e) {
			this.$refs.fupPhoto.setCustomValidity('');
			this.$refs.fupPhoto.reportValidity();

			var files = e.target.files;

			if (files && files.length > 0) {
				let file = files[0];

				this.$refs.fupPhoto.value = '';
				this.$refs.fupPhoto.blobData = null;
				
				cropImage({
					viewMode: 1,
					autoCropArea: 1
				}, { 
					width: 480, 
					height: 480 
				}, file).then(canvas => {
					this.photo = canvas.toDataURL();
					this.newPhoto = true;

					canvas.toBlob(blob => {
						this.$refs.fupPhoto.blobData = {
							blob: blob,
							name: 'photo.jpg'
						};
						
						this.$refs.lblPhoto.innerText = file.name;
					});
				}, e => {
					if(e instanceof Error)
					{
						this.$refs.fupPhoto.setCustomValidity(e.message);
						this.$refs.fupPhoto.reportValidity();
					}
				})
			}
		}
	}
}

</script>
<template>
	<div v-if="ready" class="container py-5">
		<div class="row justify-content-center">
			<div class="col-12 col-md-8 col-xl-6 my-5">
				<h1 class="display-4 text-center mb-3">Edit Rocket</h1>
				<p class="text-muted text-center mb-5">Update the properties of your rocket.</p>
					
				<form @submit.prevent="submit" @change="changed">
					<div class="form-group">
						<label class="form-label">
							Name
						</label>
						<input type="text" class="form-control" v-model="name" name="rocket-name" placeholder="e.g. LOC Goblin" required autofocus>
						<span class="invalid-feedback mt-2"></span>
						<p class="form-text text-muted small mb-0">Required</p>
					</div>

					<div class="form-group">
						<label>Build Type</label>
						<select v-model="buildType" class="form-control" required>
							<option value="" disabled selected>Choose...</option>
							<option value="RTF">RTF</option>
							<option value="Assembly-only">Assembly-only</option>
							<option value="Kit">Kit</option>
							<option value="Modified Kit">Modified Kit</option>
							<option value="Scratch Build">Scratch Built</option>
							<option value="Other">Other/Unknown</option>
						</select>
						<span class="invalid-feedback"></span>
						<p class="form-text text-muted small mb-0">Required</p>
					</div>

					<div class="row">
						<div class="col">
							<div class="form-group">
								<label>Diameter</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="99999" step="1" autocomplete="off" v-model="diameter" class="form-control" placeholder="e.g. 100" />
									<div class="input-group-append">
										<div class="input-group-text">
											mm
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
								<p class="form-text text-muted small mb-0">Required for certification flights</p>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label>Length</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="99999" step="1" autocomplete="off" v-model="length" class="form-control" placeholder="e.g. 100" />
									<div class="input-group-append">
										<div class="input-group-text">
											mm
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
								<p class="form-text text-muted small mb-0">Required for certification flights</p>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col">
							<div class="form-group">
								<label>Center of Pressure</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="99999" step="1" autocomplete="off" v-model="cp" class="form-control" placeholder="e.g. 900" />
									<div class="input-group-append">
										<div class="input-group-text">
											mm
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
								<p class="form-text text-muted small mb-0">Measured from tip of nose.</p>
							</div>
						</div>
						<div class="col">
							<div class="form-group">
								<label>Mass without motors</label>
								<div class="input-group has-validation">
									<input type="number" min="1" max="99999" step="1" autocomplete="off" v-model="mass" class="form-control" placeholder="e.g. 800" />
									<div class="input-group-append">
										<div class="input-group-text">
											g
										</div>
									</div>
									<span class="invalid-feedback"></span>
								</div>
							</div>
						</div>
					</div>
					<div class="field form-group">
						<label class="d-block">
							Upload a picture of your rocket
							<div class="dz-default dz-message py-3 my-3" v-if="photo">
								<div class="avatar avatar-xxl">
									<img ref="imgAvatar" class="avatar-img" :src="photo" alt="Photo of Rocket">
								</div>
							</div>
						</label>
						<div class="custom-file">
							<input type="file" @change="photoAdded" ref="fupPhoto" class="custom-file-input" name="rocket-photo" accept="image/*;capture=camera">
							<label ref="lblPhoto" class="custom-file-label text-muted">Choose image&hellip;</label>
							<span class="invalid-feedback"></span>
						</div>
						<span class="form-text text-muted small mb-0">Must be a jpeg or png.</span>
					</div>
					
					<div class="form-group">
						<label class="form-label mb-1">Notes</label>
						<textarea class="form-control" v-model="notes" rows="4"></textarea>
					</div>

					<button class="btn w-100 btn-primary">Save changes <span class="fe fe-check-circle"></span></button>
				</form>
			</div>
		</div>
    </div>
</template>
